import { AccountType } from "types/user";
import { IViews } from "components/product-collection/product-collection-toolbar/constants";
import { DATATEST } from "constants/integration";
import { CollectionAction, View } from "constants/product-collection";
import { ICollectionVersionStatus } from "helpers/collection-version-status";
import { formatMessage } from "lib/globalize-util";
import me from "models/me";
import { ISubscription } from "reducers/subscriptions";

interface MenuItem {
    "data-active"?: boolean;
    "data-test"?: string;
    "data-view"?: any;
    isDisabled?: boolean;
    key?: any;
    onClick?: (() => void) | ((params: any) => void);
    primaryText: string;
    subItems?: MenuItem[];
    linkTo?: string;
}

const createTimelineMenuItem = (showTimeline: boolean, onToggleTimeline: () => void) => {
    return {
        "data-test": DATATEST.ASSORTMENTS.UPDATES_BTN,
        "data-active": showTimeline,
        primaryText: formatMessage("Updates"),
        onClick: onToggleTimeline,
    };
};

const createViewMenuItems = (views: IViews[], view: View, handleChangeView: (event) => void) => {
    const viewMenuItems: MenuItem[] = [];
    views.forEach((viewItem) => {
        if (viewItem.id !== view) {
            viewMenuItems.push({
                // show inative view to toggle
                key: viewItem.id,
                "data-test": viewItem.dataTest,
                "data-view": viewItem.id,
                primaryText: formatMessage("View |name|", viewItem),
                onClick: handleChangeView,
            });
        }
    });

    return viewMenuItems;
};

const createSaveAsMenuItem = (handleOpenSaveAsDialog: () => void) => {
    return {
        "data-test": DATATEST.ASSORTMENTS.SAVE_AS_BTN,
        primaryText: formatMessage("Save As"),
        onClick: handleOpenSaveAsDialog,
    };
};

const createVersionStatusMenuItems = ({
    versionStatuses,
    currentTag,
    handleVersionStatusClicked,
}: {
    versionStatuses?: ICollectionVersionStatus[];
    currentTag?: string;
    handleVersionStatusClicked?: (event: any) => void;
}) => {
    return {
        primaryText: formatMessage("Capture size bulks"),
        "data-test": DATATEST.ASSORTMENTS.POI_TAGS.VERSION_STATUS_MENU,
        subItems: versionStatuses.map((status) => ({
            key: status.id,
            primaryText:
                status.id === currentTag ? formatMessage("Remove |label|", { label: status.label }) : status.label,
            onClick: handleVersionStatusClicked,
            "data-test": DATATEST.ASSORTMENTS.POI_TAGS.VERSION_STATUS_MENU_ITEM,
            "data-status": status.id,
        })),
    };
};

const createManageSizesSubItems = ({
    sizePackEnabled,
    handleOpenSizeSettingsDialog,
    sizingRangeEnabled,
    findingSizes,
    handleFindSizeRangesAllRows,
    sizingCurveEnabled,
    allocatingAssortment,
    handleAllocateAllRows,
    versionStatuses,
    currentTag,
    handleVersionStatusClicked,
}: {
    sizePackEnabled: boolean;
    handleOpenSizeSettingsDialog: () => void;
    sizingRangeEnabled: boolean;
    findingSizes: boolean;
    handleFindSizeRangesAllRows: () => void;
    sizingCurveEnabled: boolean;
    allocatingAssortment: boolean;
    handleAllocateAllRows: () => void;
    versionStatuses?: ICollectionVersionStatus[];
    currentTag?: string;
    handleVersionStatusClicked?: (event: any) => void;
}) => {
    const manageSizesSubItems: MenuItem[] = [];

    if (sizePackEnabled) {
        manageSizesSubItems.push({
            primaryText: formatMessage("Size Settings"),
            onClick: handleOpenSizeSettingsDialog,
            "data-test": DATATEST.ASSORTMENTS.SIZE_SETTINGS,
        });
    }

    if (sizingRangeEnabled) {
        manageSizesSubItems.push({
            "data-test": DATATEST.ASSORTMENTS.FIND_SIZE_RANGES_BTN,
            primaryText: findingSizes ? formatMessage("Stop find size ranges") : formatMessage("Find size ranges"),
            onClick: handleFindSizeRangesAllRows,
        });
    }

    if (sizingCurveEnabled) {
        manageSizesSubItems.push({
            "data-test": DATATEST.ASSORTMENTS.APPLY_SIZE_CURVES_BTN,
            primaryText: allocatingAssortment
                ? formatMessage("Stop Apply Size Curves")
                : formatMessage("Apply Size Curves"),
            onClick: handleAllocateAllRows,
        });
    }

    // add version status menu item
    if (versionStatuses?.length) {
        const versionStatusMenuItem = createVersionStatusMenuItems({
            versionStatuses,
            currentTag,
            handleVersionStatusClicked,
        });

        manageSizesSubItems.push(versionStatusMenuItem);
    }

    if (manageSizesSubItems.length) {
        return {
            primaryText: formatMessage("Manage Sizes"),
            "data-test": DATATEST.ASSORTMENTS.MANAGE_SIZES_BTN,
            subItems: manageSizesSubItems,
        };
    }

    return null;
};

const createEditableMenuItems = ({
    syncingProducts,
    onSyncProductsStop,
    onSyncProductsStart,
    sizePackEnabled,
    handleOpenSizeSettingsDialog,
    sizingRangeEnabled,
    findingSizes,
    handleFindSizeRangesAllRows,
    sizingCurveEnabled,
    allocatingAssortment,
    handleAllocateAllRows,
    isCloseoutAssortmentEnabled,
    isBrandUser,
    syncingInventoryUnits,
    handleSyncInventoryUnitsAllRows,
    hasItemId,
    onToggleAddSMUDialog,
    versionStatuses,
    currentTag,
    handleVersionStatusClicked,
    handleViewHistory,
    showJWN,
    handleOpenSendToRetailerDialog,
}: {
    syncingProducts: boolean;
    onSyncProductsStop?: () => void;
    onSyncProductsStart?: (isFiltered?: boolean) => void;
    sizePackEnabled: boolean;
    handleOpenSizeSettingsDialog: () => void;
    sizingRangeEnabled: boolean;
    findingSizes: boolean;
    handleFindSizeRangesAllRows: () => void;
    sizingCurveEnabled: boolean;
    allocatingAssortment: boolean;
    handleAllocateAllRows: () => void;
    isCloseoutAssortmentEnabled: boolean;
    isBrandUser: boolean;
    syncingInventoryUnits: boolean;
    handleSyncInventoryUnitsAllRows: (action: CollectionAction) => void;
    hasItemId: boolean;
    onToggleAddSMUDialog?: () => void;
    versionStatuses?: ICollectionVersionStatus[];
    currentTag?: string;
    handleVersionStatusClicked?: (event: any) => void;
    handleViewHistory?: () => void;
    showJWN: boolean;
    handleOpenSendToRetailerDialog: () => void;
}) => {
    const editableMenuItems: MenuItem[] = [];

    editableMenuItems.push({
        "data-test": DATATEST.ASSORTMENTS.ADD_SMU_BTN,
        primaryText: formatMessage("Add SMU"),
        isDisabled: hasItemId,
        onClick: onToggleAddSMUDialog,
    });

    editableMenuItems.push(
        syncingProducts
            ? {
                  "data-test": DATATEST.ASSORTMENTS.SYNC_PRODUCT_DATA.STOP_SYNC_BUTTON,
                  primaryText: formatMessage("Stop Product Sync"),
                  onClick: onSyncProductsStop,
              }
            : {
                  "data-test": DATATEST.ASSORTMENTS.SYNC_PRODUCT_DATA.MAIN_BUTTON,
                  primaryText: formatMessage("Sync Product Data"),
                  subItems: [
                      {
                          "data-test": DATATEST.ASSORTMENTS.SYNC_PRODUCT_DATA.ALL_PRODUCTS_BUTTON,
                          primaryText: formatMessage("All Products"),
                          onClick: onSyncProductsStart,
                      },
                      {
                          "data-test": DATATEST.ASSORTMENTS.SYNC_PRODUCT_DATA.FILTERED_PRODUCTS_BUTTON,
                          primaryText: formatMessage("Filtered Products"),
                          onClick: () => onSyncProductsStart(true),
                      },
                  ],
              }
    );

    // add manage sizes menu items
    const manageSizesMenuItems = createManageSizesSubItems({
        sizePackEnabled,
        handleOpenSizeSettingsDialog,
        sizingRangeEnabled,
        findingSizes,
        handleFindSizeRangesAllRows,
        sizingCurveEnabled,
        allocatingAssortment,
        handleAllocateAllRows,
        versionStatuses,
        currentTag,
        handleVersionStatusClicked,
    });

    if (manageSizesMenuItems) {
        editableMenuItems.push(manageSizesMenuItems);
    }

    editableMenuItems.push({
        "data-test": DATATEST.ASSORTMENTS.HISTORY_VERSION,
        primaryText: formatMessage("Version History"),
        onClick: handleViewHistory,
    });

    if (isCloseoutAssortmentEnabled && !isBrandUser) {
        editableMenuItems.push({
            "data-test": DATATEST.ASSORTMENTS.SYNC_INVENTORY_UNITS_BTN,
            primaryText: syncingInventoryUnits
                ? formatMessage("Stop Sync Inventory Units")
                : formatMessage("Sync Inventory Units"),
            onClick: handleSyncInventoryUnitsAllRows,
        });
    }

    if (showJWN) {
        editableMenuItems.push({
            key: "nordstrom",
            onClick: handleOpenSendToRetailerDialog,
            primaryText: formatMessage("Send to JWN"),
            "data-test": DATATEST.ASSORTMENTS.JWN_BTN,
        });
    }

    return editableMenuItems;
};

const createExportMenuItems = ({
    handleViewValidationErrorLog,
    handleViewExportStatusLog,
}: {
    handleViewValidationErrorLog: () => void;
    handleViewExportStatusLog: () => void;
}) => {
    const exportSubItems: MenuItem[] = [];

    exportSubItems.push({
        "data-test": DATATEST.ASSORTMENTS.EXPORT_ERROR_BTN,
        primaryText: formatMessage("View Export Errors"),
        onClick: handleViewValidationErrorLog,
    });

    exportSubItems.push({
        "data-test": DATATEST.ASSORTMENTS.EXPORT_LOG_BTN,
        primaryText: formatMessage("View Export Log"),
        onClick: handleViewExportStatusLog,
    });

    return {
        "data-test": DATATEST.ASSORTMENTS.EXPORT_MENU_BTN,
        primaryText: formatMessage("Export |info|", { info: "Errors/Logs" }),
        subItems: exportSubItems,
    };
};

const createUpcManagementMenuItems = ({
    onOpenExportUpcDialog,
    onOpenImportUpcDialog,
    canImportUpcs,
}: {
    onOpenExportUpcDialog: () => void;
    onOpenImportUpcDialog: () => void;
    canImportUpcs: boolean;
}) => {
    const manageUpcMenuItems = [
        {
            primaryText: formatMessage("Export UPCs"),
            onClick: onOpenExportUpcDialog,
            "data-test": DATATEST.ASSORTMENTS.EXPORT_UPC_BTN,
        },
    ];

    if (canImportUpcs) {
        manageUpcMenuItems.push({
            primaryText: formatMessage("Import UPCs"),
            onClick: onOpenImportUpcDialog,
            "data-test": DATATEST.ASSORTMENTS.IMPORT_UPC_BTN,
        });
    }

    return {
        primaryText: formatMessage("Manage UPCs"),
        "data-test": DATATEST.ASSORTMENTS.MANAGE_UPC_BTN,
        subItems: manageUpcMenuItems,
    };
};

interface GetMenuItemsProps {
    isMobileOrPhablet: boolean;
    isMobileOrPhaTablet: boolean;
    showTimeline: boolean;
    onToggleTimeline: () => void;
    views: IViews[];
    view: View;
    handleChangeView: (event) => void;
    editable: boolean;
    itemId?: string;
    onToggleAddSMUDialog?: () => void;
    showSaveAsAction: boolean;
    handleOpenSaveAsDialog: () => void;
    syncingProducts: boolean;
    onSyncProductsStop?: () => void;
    onSyncProductsStart?: (isFiltered?: boolean) => void;
    sizePackEnabled: boolean;
    handleOpenSizeSettingsDialog: () => void;
    sizingRangeEnabled: boolean;
    findingSizes: boolean;
    handleFindSizeRangesAllRows: () => void;
    sizingCurveEnabled: boolean;
    allocatingAssortment: boolean;
    handleAllocateAllRows: () => void;
    isCloseoutAssortmentEnabled: boolean;
    isBrandUser: boolean;
    syncingInventoryUnits: boolean;
    handleSyncInventoryUnitsAllRows: (action: CollectionAction) => void;
    versionStatuses?: ICollectionVersionStatus[];
    currentTag?: string;
    handleVersionStatusClicked?: (event: any) => void;
    handleViewHistory?: () => void;
    showExportToMerchant?: boolean;
    merchantName?: string;
    handleOpenExportToMerchantDialog?: () => void;
    enabledExportActions?: boolean;
    handleViewValidationErrorLog?: () => void;
    handleViewExportStatusLog?: () => void;
    canUseXLS: boolean;
    handleOpenExportDialog?: () => void;
    showJWN: boolean;
    showDeleteAction: boolean;
    handleOpenSendToRetailerDialog: () => void;
    subscription: ISubscription;
    handleSubscriptionAction: () => void;
    showAssortmentSettings: boolean;
    onOpenAssortmentSettings: () => void;
    showCopyAction: boolean;
    handleOpenCopyDialog: () => void;
    handleOpenDeleteDialog: () => void;
    collectionId: string;
    isUpcManagementEnabled: boolean;
    canImportUpcs: boolean;
    onOpenExportUpcDialog: () => void;
    onOpenImportUpcDialog: () => void;
}

export const getMenuItems = ({
    isMobileOrPhablet,
    isMobileOrPhaTablet,
    showTimeline,
    onToggleTimeline,
    views,
    view,
    handleChangeView,
    editable,
    itemId,
    onToggleAddSMUDialog,
    showSaveAsAction,
    handleOpenSaveAsDialog,
    syncingProducts,
    onSyncProductsStop,
    onSyncProductsStart,
    sizePackEnabled,
    handleOpenSizeSettingsDialog,
    sizingRangeEnabled,
    findingSizes,
    handleFindSizeRangesAllRows,
    sizingCurveEnabled,
    allocatingAssortment,
    handleAllocateAllRows,
    isCloseoutAssortmentEnabled,
    isBrandUser,
    syncingInventoryUnits,
    handleSyncInventoryUnitsAllRows,
    versionStatuses,
    currentTag,
    handleVersionStatusClicked,
    handleViewHistory,
    showExportToMerchant,
    merchantName,
    handleOpenExportToMerchantDialog,
    enabledExportActions,
    handleViewValidationErrorLog,
    handleViewExportStatusLog,
    canUseXLS,
    handleOpenExportDialog,
    showJWN,
    showDeleteAction,
    handleOpenDeleteDialog,
    handleOpenSendToRetailerDialog,
    subscription,
    handleSubscriptionAction,
    showAssortmentSettings,
    onOpenAssortmentSettings,
    showCopyAction,
    handleOpenCopyDialog,
    collectionId,
    isUpcManagementEnabled,
    canImportUpcs,
    onOpenExportUpcDialog,
    onOpenImportUpcDialog,
}: GetMenuItemsProps): MenuItem[] => {
    const menuItems: MenuItem[] = [];

    /* {isMobileOrPhablet && isOwner && (
                  <MenuItem primaryText={formatMessage("Share Assortment")} onClick={onToggleShareDialog} data-test={DATATEST.ASSORTMENTS.SHARE.SHARE_BTN} />
    )} */

    // Add timeline menu item
    if (isMobileOrPhaTablet) {
        const timelineMenuItem = createTimelineMenuItem(showTimeline, onToggleTimeline);
        menuItems.push(timelineMenuItem);
    }

    // Add view menu items
    if (isMobileOrPhablet) {
        const viewMenuItems = createViewMenuItems(views, view, handleChangeView);
        menuItems.push(...viewMenuItems);
    }

    // Add save as menu item
    if (showSaveAsAction) {
        const saveAsMenuItem = createSaveAsMenuItem(handleOpenSaveAsDialog);
        menuItems.push(saveAsMenuItem);
    }

    // Add editable menu items
    if (editable) {
        const editableMenuItems = createEditableMenuItems({
            syncingProducts,
            onSyncProductsStop,
            onSyncProductsStart,
            sizePackEnabled,
            handleOpenSizeSettingsDialog,
            sizingRangeEnabled,
            findingSizes,
            handleFindSizeRangesAllRows,
            sizingCurveEnabled,
            allocatingAssortment,
            handleAllocateAllRows,
            isCloseoutAssortmentEnabled,
            isBrandUser,
            syncingInventoryUnits,
            handleSyncInventoryUnitsAllRows,
            hasItemId: !!itemId,
            onToggleAddSMUDialog,
            versionStatuses,
            currentTag,
            handleVersionStatusClicked,
            handleViewHistory,
            showJWN,
            handleOpenSendToRetailerDialog,
        });

        menuItems.push(...editableMenuItems);
    }

    if (showExportToMerchant && !isBrandUser) {
        menuItems.push({
            key: "export-merchant",
            "data-test": DATATEST.ASSORTMENTS.EXPORT_MERCHANT_BTN,
            primaryText: formatMessage("Export to |merchantName|", { merchantName }),
            onClick: handleOpenExportToMerchantDialog,
        });
    }

    if (enabledExportActions) {
        const exportMenuItems = createExportMenuItems({
            handleViewValidationErrorLog,
            handleViewExportStatusLog,
        });

        menuItems.push(exportMenuItems);
    }

    if (canUseXLS) {
        menuItems.push({
            key: "all",
            "data-test": DATATEST.ASSORTMENTS.EXPORT_BTN,
            primaryText: formatMessage("Export"),
            onClick: handleOpenExportDialog,
        });
    }

    menuItems.push({
        "data-test": DATATEST.ASSORTMENTS.NOTIFICATIONS_BTN,
        primaryText: subscription ? formatMessage("Suspend notifications") : formatMessage("Opt-in to notifications"),
        onClick: handleSubscriptionAction,
    });

    if (showAssortmentSettings) {
        menuItems.push({
            primaryText: formatMessage("Market Settings"),
            onClick: onOpenAssortmentSettings,
            "data-test": DATATEST.ASSORTMENTS.ASSORTMENT_SETTINGS_BTN,
        });
    }

    if (showCopyAction) {
        menuItems.push({
            primaryText: formatMessage("Copy Assortment"),
            onClick: handleOpenCopyDialog,
            "data-test": DATATEST.ASSORTMENTS.COPY_ASSORTMENT_BTN,
        });
    }

    if (showDeleteAction) {
        menuItems.push({
            primaryText: formatMessage("Delete Assortment"),
            onClick: handleOpenDeleteDialog,
            "data-test": DATATEST.ASSORTMENTS.DELETE_ASSORTMENT_BTN,
        });
    }

    if (isUpcManagementEnabled) {
        const upcManagementMenuItems = createUpcManagementMenuItems({
            canImportUpcs,
            onOpenImportUpcDialog,
            onOpenExportUpcDialog,
        });

        menuItems.push(upcManagementMenuItems);
    }

    if (me.account_type === AccountType.NUORDER) {
        menuItems.push({
            "data-test": DATATEST.ASSORTMENTS.OPERATION_HISTORY_BTN,
            primaryText: formatMessage("Operation History"),
            onClick: handleViewHistory,
            linkTo: `/assortment/${collectionId}/revisions?cartView=${View.Dataloss}&mode=${View.CART}`,
        });
    }

    return menuItems;
};
